'use strict'

import React, { useCallback } from 'react'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

const FORM_TITLE = '時間形式変換'
const TIME_FORMAT_PRESETS = [
  { value: 'hh:mm', label: 'hh:mm' },
  { value: 'hh:mm:ss', label: 'hh:mm:ss' },
  { value: 'h:mm', label: 'h:mm' },
  { value: 'mm:ss', label: 'mm:ss' },
  { value: '__custom__', label: 'カスタム' }
]
const CUSTOM_PLACEHOLDER = '例: h:mm:ss'
const ERRORS = {
  FILE_TYPE_REQUIRED: 'ファイル種別を選択してください',
  COLUMN_NAME_REQUIRED: '項目名を選択してください',
  TIME_FORMAT_REQUIRED: '時間形式を選択してください',
  CUSTOM_FORMAT_REQUIRED: 'カスタム形式を入力してください',
  CUSTOM_FORMAT_HH: 'カスタム形式には時（H/h または HH/hh）を含めてください',
  CUSTOM_FORMAT_MM: 'カスタム形式には分（m または mm）を含めてください'
}

/**
 * カスタム形式に H/HH（大文字小文字問わず）と m/mm が含まれるか検証（SR-132, SR-133）
 *
 * @param {string} format
 * @returns {{ valid: boolean; error?: string }}
 */
function validateCustomTimeFormat(format) {
  const s = String(format ?? '').trim()
  if (!s) return { valid: false, error: ERRORS.CUSTOM_FORMAT_REQUIRED }
  const hasHour = /[Hh]/.test(s)
  const hasMinute = /m{1,2}/.test(s)
  if (!hasHour) return { valid: false, error: ERRORS.CUSTOM_FORMAT_HH }
  if (!hasMinute) return { valid: false, error: ERRORS.CUSTOM_FORMAT_MM }
  return { valid: true }
}

/**
 * @typedef {{ SourceTimeFormat?: string }} SourceTimeFormat
 *
 * @typedef {{
 *   MappingType: 'time_format'
 *   MappingFileType?: string
 *   MappingColumnName?: string
 *   IsCustom?: boolean
 *   TimeFormat?: { SourceTimeFormat: string }
 * }} MappingInfoTimeFormat
 */

/**
 * MappingInfo time_format → フォーム用（DB→UI）
 *
 * @param {MappingInfoTimeFormat | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ mappingFileType: string; mappingColumnName: string; timeFormatPreset: string; customFormat: string }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const tf = initial?.TimeFormat ?? initial?.time_format
  const sourceFormat = tf?.SourceTimeFormat ?? tf?.sourceTimeFormat ?? initial?.sourceTimeFormat ?? ''
  const preset = TIME_FORMAT_PRESETS.find((p) => p.value !== '__custom__' && p.value === sourceFormat)
  const firstFile = fileTypes?.[0]
  const isCustom = Boolean(initial?.IsCustom ?? initial?.isCustom ?? (!preset && sourceFormat))
  return {
    mappingFileType:
      initial?.MappingFileType ?? initial?.mappingFileType ?? initial?.fileType ?? firstFile?.mappingFileType ?? '',
    mappingColumnName:
      initial?.MappingColumnName ?? initial?.mappingColumnName ?? initial?.field ?? firstFile?.headers?.[0] ?? '',
    timeFormatPreset: isCustom ? '__custom__' : (preset?.value ?? ''),
    customFormat: isCustom ? sourceFormat : ''
  }
}

/**
 * フォーム → MappingInfo time_format（UI→DB）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; timeFormatPreset: string; customFormat: string }} formData
 * @returns {{
 *   MappingType: 'time_format'
 *   MappingFileType: string
 *   MappingColumnName: string
 *   IsCustom: boolean
 *   TimeFormat: { SourceTimeFormat: string }
 * }}
 */
export function formDataToMappingInfo(formData) {
  const isCustom = formData.timeFormatPreset === '__custom__'
  const effectiveFormat = isCustom ? formData.customFormat.trim() : formData.timeFormatPreset
  return {
    MappingType: 'time_format',
    MappingFileType: formData.mappingFileType,
    MappingColumnName: formData.mappingColumnName,
    IsCustom: isCustom,
    TimeFormat: {
      SourceTimeFormat: effectiveFormat
    }
  }
}

/**
 * バリデーション（SR-145, SR-147, SR-148）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; timeFormatPreset: string; customFormat: string }} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateTimeFormatForm(formData) {
  const errors = /** @type {Record<string, string[]>} */ ({})
  if (!formData.mappingFileType?.trim()) {
    errors.mappingFileType = [ERRORS.FILE_TYPE_REQUIRED]
  }
  if (!formData.mappingColumnName?.trim()) {
    errors.mappingColumnName = [ERRORS.COLUMN_NAME_REQUIRED]
  }
  const isCustom = formData.timeFormatPreset === '__custom__'
  const effectiveFormat = isCustom ? formData.customFormat.trim() : formData.timeFormatPreset
  if (!formData.timeFormatPreset) {
    errors.timeFormat = [ERRORS.TIME_FORMAT_REQUIRED]
  } else if (isCustom && !effectiveFormat) {
    errors.timeFormat = [ERRORS.CUSTOM_FORMAT_REQUIRED]
  } else if (isCustom) {
    const customCheck = validateCustomTimeFormat(formData.customFormat)
    if (!customCheck.valid) {
      errors.timeFormat = [customCheck.error ?? ERRORS.CUSTOM_FORMAT_REQUIRED]
    }
  }
  return Object.keys(errors).length > 0 ? errors : null
}

/**
 * 時間形式変換モーダル本体（SR-118〜SR-121）
 *
 * @param {{
 *   fileTypes: import('../extra/types').FileTypeInfo[]
 *   initialMappingInfo: MappingInfoTimeFormat | null
 *   formData: { mappingFileType: string; mappingColumnName: string; timeFormatPreset: string; customFormat: string }
 *   errors: Record<string, string[]>
 *   onChange: (fd: object, setErrors: (e: Record<string, string[]>) => void) => void
 * }} props
 */
export const TimeFormatForm = (props) => {
  const { fileTypes = [], formData, errors, onChange } = props

  const selectedFile = fileTypes.find((f) => f.mappingFileType === formData.mappingFileType)
  const columnOptions = (selectedFile?.headers ?? []).map((h) => ({ label: h, value: h }))
  const isColumnDisabled = !formData.mappingFileType
  const isCustom = formData.timeFormatPreset === '__custom__'
  const effectiveFormat = isCustom ? formData.customFormat.trim() : formData.timeFormatPreset

  const handleFileTypeChange = useCallback(
    (e) => {
      const mappingFileType = e.target.value ?? ''
      onChange({ ...formData, mappingFileType, mappingColumnName: '' }, () => ({}))
    },
    [formData, onChange]
  )

  const handleColumnChange = useCallback(
    (e) => {
      const mappingColumnName = e.target.value ?? ''
      onChange({ ...formData, mappingColumnName }, () => ({}))
    },
    [formData, onChange]
  )

  const handlePresetChange = useCallback(
    (e) => {
      const timeFormatPreset = e.target.value ?? ''
      onChange({ ...formData, timeFormatPreset }, () => ({}))
    },
    [formData, onChange]
  )

  const handleCustomFormatChange = useCallback(
    (e) => {
      const customFormat = e.target.value ?? ''
      onChange({ ...formData, customFormat }, () => ({}))
    },
    [formData, onChange]
  )

  return (
    <div className="text-h7">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#666] mb-2">
            ファイル種別を選択 <span className="red--text">*</span>
          </label>
          <ChrofyCustomSelect
            id="time-format-file-type"
            name="time-format-file-type"
            value={formData.mappingFileType}
            onChange={handleFileTypeChange}
            className="w-full"
            options={(fileTypes || []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))}
            disabled={false}
            hasNoSelect={true}
            searchable
            isError={Boolean(errors.mappingFileType?.length)}
            data-testid="time-format-file-type"
          />
          <ChrofyShowErrors field="mappingFileType" errors={errors} />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#666] mb-2">
            項目選択 <span className="red--text">*</span>
          </label>
          <ChrofyCustomSelect
            id="time-format-column-name"
            name="time-format-column-name"
            value={formData.mappingColumnName}
            onChange={handleColumnChange}
            className={isColumnDisabled ? 'opacity-60 w-full' : 'w-full'}
            options={columnOptions}
            disabled={isColumnDisabled}
            hasNoSelect={true}
            searchable
            isError={Boolean(errors.mappingColumnName?.length)}
            data-testid="time-format-column-name"
          />
          <ChrofyShowErrors field="mappingColumnName" errors={errors} />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#666] mb-2">
            元の時間形式 <span className="red--text">*</span>
          </label>
          <ChrofyCustomSelect
            id="time-format-preset"
            name="time-format-preset"
            value={formData.timeFormatPreset}
            onChange={handlePresetChange}
            className="w-full"
            options={TIME_FORMAT_PRESETS.map((p) => ({ label: p.label, value: p.value }))}
            disabled={false}
            hasNoSelect={true}
            noSelectText="形式を選択"
            isError={Boolean(errors.timeFormat?.length)}
            data-testid="time-format-preset"
          />
          {isCustom && (
            <input
              type="text"
              data-testid="time-format-custom"
              className="text-input mt-2 w-full text-left"
              placeholder={CUSTOM_PLACEHOLDER}
              value={formData.customFormat}
              onChange={handleCustomFormatChange}
            />
          )}
          <ChrofyShowErrors field="timeFormat" errors={errors} />
        </div>

        <div className="bg-[#e8f4f8] p-3 rounded-md border-l-[3px] border-l-[#3150A5]">
          <div className="text-[13px] text-[#666]">変換後の形式(システム管理)</div>
          <div className="text-base font-semibold text-[#3150A5]">10進数(時間)</div>
          <div className="text-[12px] text-[#666] mt-1">例: 2:30 → 2.5時間</div>
        </div>
      </div>
    </div>
  )
}
